import { Box, Typography } from "@mui/material";
import type { DateTime } from "luxon";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { FONT_SIZE, INK, NEUTRAL, OVERTIME } from "../../../../theme/tokens";
import MetaChip, { type MetaChipCustomTone, type MetaChipTone } from "../../../UIToolkit/Badges/MetaChip";
import SystemButton from "../../../UIToolkit/SystemButton/SystemButton";
import { GapsRoleBadge } from "../GapsRoleBadge";
import { ROW_MARKER_SX } from "../gapsRowLayout";
import { GapsJobBadge } from "../sheets/GapsJobBadge";
import type { GapsApplicant } from "./useGapsApplicants";

type Props = {
	applicant: GapsApplicant;
	/** Hours in the home's compact form (`9a-3p`) — how a partial pickup and a double name theirs. */
	formatHours: (start: DateTime, end: DateTime) => string;
	onApprove: (applicant: GapsApplicant) => void;
	dataTestId: string;
};

/**
 * One person who asked for a shift, and the Approve that gives it to them (P3-12).
 *
 * The line prints only exceptions — a partial pickup, agency staff, a double, the overtime it adds — so a clean
 * candidate is just a name and the eye stops where a decision gets harder. The marks sit on their own line under the
 * name rather than in fixed columns beside it: at 320px a single line left the name no room at all.
 *
 * Approve is labelled, not a tick: it is a write that closes the shift for everyone else, so the word is the affordance.
 * It opens the confirm sheet; nothing is written from here.
 */
export const GapsApplicantLine: FC<Props> = ({ applicant, formatHours, onApprove, dataTestId }) => {
	const { t } = useTranslation();
	const { nurse, job, partial, isAgency, otherShift, overtimeHours } = applicant;

	// The home's own exception chips (the staff row's OT and training marks): one family, uppercase and tracked.
	const marks: { key: string; label: string; tone?: MetaChipTone; customTone?: MetaChipCustomTone }[] = [];
	if (partial !== null) marks.push({ key: "partial", label: t("gaps.requests.partial", { hours: formatHours(partial.start, partial.end) }), tone: "warning" });
	if (isAgency) marks.push({ key: "agency", label: t("gaps.requests.agency"), tone: "info" });
	// Only a double the phone can see: for a day it has not loaded, the line claims nothing (the confirm says "not known").
	if (otherShift.isKnown && otherShift.shift !== undefined) {
		const { startTime, endTime } = otherShift.shift;
		marks.push({ key: "double", label: t("gaps.requests.doubleShift", { hours: formatHours(startTime, endTime) }), tone: "warning" });
	}
	if (overtimeHours !== undefined && overtimeHours > 0) {
		marks.push({
			key: "overtime",
			label: t("gaps.requests.overtime", { hours: Number(overtimeHours.toFixed(1)) }),
			customTone: { bg: OVERTIME.BG, ink: OVERTIME.INK },
		});
	}

	return (
		<Box
			data-testid={dataTestId}
			sx={{ display: "flex", alignItems: "center", gap: 1, px: 1.5, py: 0.875, "& + &": { borderTop: `1px solid ${NEUTRAL[150]}` } }}
		>
			{job !== undefined ? (
				<GapsJobBadge group={{ jobLabel: job.displayName, isJobSection: true, jobId: job.id }} dense dataTestId={`${dataTestId}-job`} />
			) : (
				nurse !== undefined && <GapsRoleBadge role={nurse.role} size="row" />
			)}
			<Box sx={{ flex: 1, minWidth: 0 }}>
				<Typography
					data-testid={`${dataTestId}-name`}
					sx={{ fontSize: FONT_SIZE.SECONDARY, fontWeight: 600, color: INK.PRIMARY, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
				>
					{applicant.name}
				</Typography>
				{marks.length > 0 && (
					<Box data-testid={`${dataTestId}-marks`} sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", columnGap: 0.75, rowGap: 0.25, mt: 0.25 }}>
						{marks.map((mark) => (
							<MetaChip
								key={mark.key}
								label={mark.label}
								size="small"
								tone={mark.tone}
								customTone={mark.customTone}
								sx={ROW_MARKER_SX}
								dataTestId={`${dataTestId}-${mark.key}`}
							/>
						))}
					</Box>
				)}
			</Box>
			<SystemButton
				buttonVariant="secondary"
				size="compact"
				touchTarget
				noFullWidthMobile
				onClick={() => onApprove(applicant)}
				aria-label={t("gaps.requests.approveName", { name: applicant.name })}
				data-testid={`${dataTestId}-approve`}
				sx={{ flex: "none" }}
			>
				{t("gaps.requests.approve")}
			</SystemButton>
		</Box>
	);
};
